/**
 * @fileoverview Palo Alto user onboarding experience
 *
 * A guided welcome flow for Palo Alto colleagues that introduces them to
 * the platform with a clear 4-step journey:
 * 1. Import your accounts (My Lists)
 * 2. Analyze vendor footprint (VCA)
 * 3. Explore spending flows (Sankey)
 * 4. Assess security posture (Zero Trust)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks, Crown, GitPullRequest, Shield,
  ChevronRight, CheckCircle, Sparkles, ArrowRight,
  Copy, Upload, Play, Target, MessageSquare, Send,
  ThumbsUp, Lightbulb, Bug
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './PaloWelcome.css';

const ONBOARDING_KEY = 'palo_onboarding_state';

const JOURNEY_STEPS = [
  {
    id: 'accounts',
    number: 1,
    title: 'Import Your Accounts',
    subtitle: 'Start by adding your territory',
    description: 'Copy and paste your Salesforce account list to see data filtered to your territory. This powers everything else.',
    icon: ListChecks,
    route: '/account-lists',
    cta: 'Add My Accounts',
    ctaIcon: Copy,
    color: '#3b82f6',
    tips: [
      'Paste directly from Salesforce or Excel',
      'We auto-match department names',
      'Your list syncs across all views'
    ]
  },
  {
    id: 'vendor',
    number: 2,
    title: 'Analyze Palo Alto Footprint',
    subtitle: 'See where we are today',
    description: 'Explore Palo Alto Networks presence across California agencies. See spending trends, competitive positioning, and renewal opportunities.',
    icon: Crown,
    route: '/vendor-analysis?vendor=Palo%20Alto%20Networks',
    cta: 'View Vendor Analysis',
    ctaIcon: Target,
    color: '#f59e0b',
    tips: [
      'Pre-filtered to Palo Alto Networks',
      'Drill into any agency for details',
      'Track renewals and take-out opportunities'
    ]
  },
  {
    id: 'spending',
    number: 3,
    title: 'Explore Spending Flows',
    subtitle: 'Follow the money',
    description: 'Visualize how technology spend flows from departments through vendors to products. Click any node to drill down.',
    icon: GitPullRequest,
    route: '/sankey',
    cta: 'See Spending Flow',
    ctaIcon: Play,
    color: '#10b981',
    tips: [
      'Interactive Sankey diagram',
      'Filter by your account list',
      'Export data for proposals'
    ]
  },
  {
    id: 'security',
    number: 4,
    title: 'Assess Security Posture',
    subtitle: 'Find the gaps',
    description: 'Use the Zero Trust assessment framework to identify security gaps and map them to Palo Alto solutions.',
    icon: Shield,
    route: '/zero-trust',
    cta: 'Start Assessment',
    ctaIcon: ArrowRight,
    color: '#8b5cf6',
    tips: [
      'CISA Zero Trust maturity model',
      'Map gaps to PANW solutions',
      'Generate executive summaries'
    ]
  }
];

function StepCard({ step, isCompleted, isActive, onStart }) {
  const Icon = step.icon;
  const CtaIcon = step.ctaIcon;

  return (
    <div
      className={`pw-step-card ${isCompleted ? 'pw-step-completed' : ''} ${isActive ? 'pw-step-active' : ''}`}
      style={{ '--step-color': step.color }}
    >
      <div className="pw-step-header">
        <div className="pw-step-number">
          {isCompleted ? <CheckCircle size={20} /> : step.number}
        </div>
        <div className="pw-step-icon">
          <Icon size={24} />
        </div>
      </div>

      <div className="pw-step-content">
        <h3>{step.title}</h3>
        <p className="pw-step-subtitle">{step.subtitle}</p>
        <p className="pw-step-description">{step.description}</p>

        <ul className="pw-step-tips">
          {step.tips.map((tip, i) => (
            <li key={i}>
              <CheckCircle size={12} />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </div>

      <button
        className="pw-step-cta"
        onClick={() => onStart(step)}
        disabled={isCompleted}
      >
        {isCompleted ? (
          <>
            <CheckCircle size={16} />
            <span>Completed</span>
          </>
        ) : (
          <>
            <CtaIcon size={16} />
            <span>{step.cta}</span>
            <ChevronRight size={16} />
          </>
        )}
      </button>
    </div>
  );
}

function ProgressBar({ completedSteps, totalSteps }) {
  const percentage = (completedSteps / totalSteps) * 100;

  return (
    <div className="pw-progress">
      <div className="pw-progress-bar">
        <div
          className="pw-progress-fill"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="pw-progress-text">
        {completedSteps} of {totalSteps} steps completed
      </span>
    </div>
  );
}

const FEEDBACK_TYPES = [
  { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: '#f59e0b' },
  { id: 'feedback', label: 'General Feedback', icon: ThumbsUp, color: '#10b981' },
  { id: 'bug', label: 'Report Issue', icon: Bug, color: '#ef4444' },
];

function FeedbackWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('feature');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      await apiClient.post('/api/feedback', {
        type: feedbackType,
        message: message.trim(),
        page: 'palo_welcome',
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }
      });
      setSubmitted(true);
      setMessage('');
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        className="pw-feedback-trigger"
        onClick={() => setIsOpen(true)}
      >
        <MessageSquare size={18} />
        <span>Share Feedback</span>
      </button>
    );
  }

  return (
    <div className="pw-feedback-widget">
      <div className="pw-feedback-header">
        <MessageSquare size={18} />
        <span>Share Your Feedback</span>
        <button
          className="pw-feedback-close"
          onClick={() => setIsOpen(false)}
        >
          ×
        </button>
      </div>

      {submitted ? (
        <div className="pw-feedback-success">
          <CheckCircle size={32} />
          <p>Thanks for your feedback!</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="pw-feedback-types">
            {FEEDBACK_TYPES.map(type => {
              const TypeIcon = type.icon;
              return (
                <button
                  key={type.id}
                  type="button"
                  className={`pw-feedback-type ${feedbackType === type.id ? 'active' : ''}`}
                  onClick={() => setFeedbackType(type.id)}
                  style={{ '--type-color': type.color }}
                >
                  <TypeIcon size={14} />
                  <span>{type.label}</span>
                </button>
              );
            })}
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              feedbackType === 'feature'
                ? "What feature would help you most? Describe your ideal workflow..."
                : feedbackType === 'bug'
                ? "What went wrong? Include any error messages..."
                : "What's on your mind? Any suggestions for improvement..."
            }
            rows={4}
            className="pw-feedback-input"
          />

          {error && <p className="pw-feedback-error">{error}</p>}

          <button
            type="submit"
            className="pw-feedback-submit"
            disabled={!message.trim() || submitting}
          >
            {submitting ? (
              <span>Sending...</span>
            ) : (
              <>
                <Send size={14} />
                <span>Submit Feedback</span>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

export default function PaloWelcome() {
  const navigate = useNavigate();
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const saved = localStorage.getItem(ONBOARDING_KEY);
      return saved ? JSON.parse(saved).completed || [] : [];
    } catch {
      return [];
    }
  });
  const [dismissed, setDismissed] = useState(() => {
    try {
      const saved = localStorage.getItem(ONBOARDING_KEY);
      return saved ? JSON.parse(saved).dismissed || false : false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(ONBOARDING_KEY, JSON.stringify({
      completed: completedSteps,
      dismissed,
    }));
  }, [completedSteps, dismissed]);

  const handleStartStep = (step) => {
    // Mark as completed when they click
    if (!completedSteps.includes(step.id)) {
      setCompletedSteps([...completedSteps, step.id]);
    }
    navigate(step.route);
  };

  const handleSkipOnboarding = () => {
    setDismissed(true);
    navigate('/account-lists');
  };

  const handleResetOnboarding = () => {
    setCompletedSteps([]);
    setDismissed(false);
  };

  // Find the first incomplete step
  const activeStepIndex = JOURNEY_STEPS.findIndex(
    step => !completedSteps.includes(step.id)
  );

  return (
    <div className="pw-page">
      {/* Hero Section */}
      <header className="pw-hero">
        <div className="pw-hero-badge">
          <Sparkles size={14} />
          <span>Welcome to TekStak</span>
        </div>
        <h1>California Procurement Intelligence</h1>
        <p>
          Your guided tour to understanding technology spend across California
          state agencies. Follow these four steps to get started.
        </p>
        <ProgressBar
          completedSteps={completedSteps.length}
          totalSteps={JOURNEY_STEPS.length}
        />
      </header>

      {/* Journey Steps */}
      <section className="pw-journey">
        <div className="pw-steps-grid">
          {JOURNEY_STEPS.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              isCompleted={completedSteps.includes(step.id)}
              isActive={index === activeStepIndex}
              onStart={handleStartStep}
            />
          ))}
        </div>
      </section>

      {/* Quick Start CTA */}
      {completedSteps.length === 0 && (
        <section className="pw-quick-start">
          <div className="pw-quick-start-content">
            <div className="pw-quick-start-icon">
              <ListChecks size={32} />
            </div>
            <div>
              <h2>Ready to start?</h2>
              <p>The fastest way to see value is to import your account list. Just paste from Salesforce.</p>
            </div>
            <button
              className="pw-primary-cta"
              onClick={() => handleStartStep(JOURNEY_STEPS[0])}
            >
              <Upload size={18} />
              <span>Import My Accounts</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </section>
      )}

      {/* Feedback Section */}
      <section className="pw-feedback-section">
        <FeedbackWidget />
      </section>

      {/* Footer Actions */}
      <footer className="pw-footer">
        <button
          className="pw-skip-btn"
          onClick={handleSkipOnboarding}
        >
          Skip tour and explore on my own
        </button>
        {completedSteps.length > 0 && (
          <button
            className="pw-reset-btn"
            onClick={handleResetOnboarding}
          >
            Reset progress
          </button>
        )}
      </footer>
    </div>
  );
}

// Export check for whether to show welcome page
export function shouldShowPaloWelcome() {
  try {
    const saved = localStorage.getItem(ONBOARDING_KEY);
    if (!saved) return true;
    const state = JSON.parse(saved);
    return !state.dismissed && (state.completed || []).length < 2;
  } catch {
    return true;
  }
}
